import React, { useState, useMemo, useRef, useEffect } from 'react';
import { useDispatch } from 'react-redux';
import moment from 'moment';
import { isMobile } from 'react-device-detect';
import clsx from 'clsx';
import { seekPlayerTime } from 'store/secondaryChannelVideoSlice';

import { EditIcon } from 'components/icons/EditIcon';
import { TrashIcon } from 'components/icons/TrashIcon';
import EditableField from './Timeline/EditableField';

import { updateSpicificNote } from 'api/notesAPI';

type CommentItemType = {
  videoHashId: string;
  hashId: string;
  content: string;
  user: any;
  displayAvatar: any;
  navigateLink: any;
  time: number;
  userData: any;
  handleEdit: (hashId: string) => void;
  isEditing: boolean;
  handleCancelEdit: () => void;
  onDelete: (hashId: string) => void;
  item: any;
  hoveredId?: string;
  handleHoveredId?: (hashId: string) => void;
};

const formatTime = (time: number) => {
  return time
    ? time > 3600
      ? moment.duration(time, 'seconds').format('h:mm:ss', { trim: false })
      : moment.duration(time, 'seconds').format('m:ss', { trim: false })
    : '';
};

function NoteItem({
  videoHashId,
  hashId,
  content,
  user,
  displayAvatar,
  navigateLink,
  time,
  userData,
  handleEdit,
  isEditing,
  handleCancelEdit,
  onDelete,
  item,
  hoveredId,
  handleHoveredId
}: CommentItemType) {
  const [hovered, setHovered] = useState(false);
  const dispatch = useDispatch();
  const isActivePlayer = sessionStorage.getItem('playerActive');
  const [editedContent, setEditedContent] = useState(content);
  const channelId = useMemo(() => {
    const channelIdRegex = /\/channel\/([^/]+)/;
    const channelMatch = location.pathname.match(channelIdRegex);
    return channelMatch ? channelMatch[1] : 'home';
  }, [location.pathname]);

  const navigateToVideo = (time: any) => {
    if (isActivePlayer === 'true') {
      dispatch(seekPlayerTime({ channelHashId: channelId, time: time }));
    } else {
      navigateLink(time);
    }
  };

  // const handleClick = useCallback(() => {
  //   if (navigateLink) {
  //     navigateLink();
  //   }
  //   dispatch(seekPlayerTime({ channelHashId: channelId, time: time }));
  // }, [navigateLink, dispatch, time, videoShotIdx]);
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  useEffect(() => {
    if (isEditing && textareaRef.current) {
      const textarea = textareaRef.current;
      textarea.style.height = 'auto';
      textarea.style.height = `${textarea.scrollHeight}px`;
      textareaRef.current.focus();
      textarea.setSelectionRange(textarea.value.length, textarea.value.length);
    }
  }, [isEditing]);

  return (
    <div
      className={`flex items-center pl-4 font-lato relative mr-4 pr-4 cursor-pointer ${
        isEditing ? 'bg-[#f2c74433] py-1' : ''
      }`}
      onMouseEnter={() => !isMobile && setHovered(true)}
      onMouseLeave={() => !isMobile && setHovered(false)}
      onTouchStart={() => {
        if (isMobile && handleHoveredId) {
          handleHoveredId(hashId);
        }
      }}
    >
      <div className="flex items-center w-full">
        <div className="mr-[10px] mb-auto shrink-0 text-right leading-[1.4]">
          {displayAvatar ? (
            <img
              className="rounded-full mt-[2px]"
              src={
                user.avatar
                  ? user.avatar.includes('http')
                    ? user.avatar
                    : userData.workspace?.url + user.avatar
                  : '/assets/img/unknown-avatar.png'
              }
              width={36}
              height={36}
              alt={`${user?.display_name} - Image`}
            />
          ) : (
            <div className="mt-[2px]">
              <p
                className={clsx('text-xs font-normal w-11 -ml-2 cursor-pointer text-[#1664A3]', {
                  visible: hovered || isMobile,
                  invisible: !hovered && !isMobile
                })}
                onClick={() => navigateToVideo(time)}
                onTouchEnd={() => navigateToVideo(time)}
              >
                {formatTime(time)}
              </p>
            </div>
          )}
        </div>
        <div className="w-full grow shrink -mr-2 -ml-4 pr-2 pl-4 leading-[1.4] flex flex-wrap flex-col">
          {displayAvatar ? (
            <div>
              <a className="mr-[5px] text-iconColor font-lato no-underline font-black text-[15px]">
                {user.display_name || 'Unknown'}
              </a>
              <small
                className="text-xs font-normal text-[#1664A3] cursor-pointer"
                onClick={() => navigateToVideo(time)}
              >
                {formatTime(time)}
              </small>
            </div>
          ) : (
            ''
          )}
          <p
            className={`text-sm font-lato font-normal text-black leading-[1.4] break-words ${
              isEditing ? 'hidden' : 'visible'
            }`}
          >
            {content}
          </p>
          {isEditing && (
            <EditableField
              value={editedContent}
              onChange={setEditedContent}
              onCancel={() => {
                handleCancelEdit();
                setHovered(false);
              }}
              onSave={async () => {
                try {
                  await updateSpicificNote(videoHashId, hashId, { content: editedContent, time: time ? time : 0 });
                  handleCancelEdit();
                } catch (error) {
                  handleCancelEdit();
                }
              }}
              ref={textareaRef}
            />
          )}
        </div>
      </div>
      {hovered && !isMobile && (
        <div
          className={clsx(
            'cursor-pointer absolute right-2 -top-1 bg-white flex p-[2px] leading-none rounded-md shadow-default z-50 text-sm'
          )}
        >
          <div
            className="group w-8 h-8 flex items-center justify-center rounded bg-none hover:bg-iconColor hover:bg-opacity-5"
            onClick={() => {
              handleEdit(hashId);
            }}
          >
            <EditIcon className="w-3 h-4 opacity-70 group-hover:opacity-100" />
          </div>
          <div
            className="group w-8 h-8 flex items-center justify-center rounded bg-none hover:bg-iconColor hover:bg-opacity-5"
            onClick={() => onDelete(hashId)}
          >
            <TrashIcon className="w-4 h-4 fill-[#dd2c3c] opacity-70 group-hover:opacity-100" />
          </div>
        </div>
      )}
      {isMobile && hoveredId === hashId && (
        <div
          className={clsx(
            'cursor-pointer absolute right-2 -top-1 bg-white flex p-[2px] leading-none rounded-md shadow-default z-50 text-sm'
          )}
        >
          <div
            className="group w-8 h-8 flex items-center justify-center rounded bg-none hover:bg-iconColor hover:bg-opacity-5"
            onClick={() => {
              handleEdit(hashId);
            }}
          >
            <EditIcon className="w-3 h-4 opacity-70 group-hover:opacity-100" />
          </div>
          <div
            className="group w-8 h-8 flex items-center justify-center rounded bg-none hover:bg-iconColor hover:bg-opacity-5"
            onClick={() => onDelete(hashId)}
          >
            <TrashIcon className="w-4 h-4 fill-[#dd2c3c] opacity-70 group-hover:opacity-100" />
          </div>
        </div>
      )}
    </div>
  );
}

export default NoteItem;
